import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/ParticipantList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import API from '../../api/axiosInstance';
import { dedupeOrgNamesForPerson, pickMainOrgName } from '../../utils/dedupePersonRoles';
import { clearCaddieOptionsCache } from '../../utils/caddieFormioUtils';
import { formatAccreditationCategoryLabel } from '../../utils/invitationTypeFormioUtils';
import ParticipantGuestRow from './ParticipantGuestRow';
import AutoLoginConfirmModal from './AutoLoginConfirmModal';
import DeleteConfirmModal, { useDeleteConfirm } from './DeleteConfirmModal';
import { GoldLoader } from '../shared/Loaders';
import {
  faPen,
  faEllipsisV,
  faTrashAlt,
  // DISABLED: faHotel, faClone — Manage Hotel / Duplicate menu items hidden
  // DISABLED 2026-08-10: faEnvelope — Email To menu item hidden
  faFileCircleCheck,
  faFileCircleQuestion,
  faUserShield,
  faSignOutAlt,
  faFileArchive,
  faBuilding,
  faUndo,
  faSortUp,
  faSortDown,
  faSort,
  faClipboardCheck,
  faChevronRight,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';

const LOCKED_GROUP_ENDPOINTS = {
  Athlete: '/ems/participants/group/athletes',
  Caddie: '/ems/participants/group/caddies',
};

export const getOnsiteType = (p) => {
  const groups = Array.isArray(p.group_id) ? p.group_id : (p.group_id ? [p.group_id] : []);
  const names = groups.map(g => g?.group_name?.toLowerCase() || '');
  if (names.some(n => n.includes('caddie')))  return 'caddie';
  if (names.some(n => n.includes('athlete'))) return 'athlete';
  return null;
};

/** competition_id may be ObjectId, populated doc, or array of either */
export function getParticipantCompetitionIds(p) {
  if (!p?.competition_id) return [];
  const raw = Array.isArray(p.competition_id) ? p.competition_id : [p.competition_id];
  return raw
    .map((c) => {
      if (!c) return null;
      if (typeof c === 'object' && c._id) return String(c._id);
      if (typeof c === 'object') return null;
      return String(c);
    })
    .filter(Boolean);
}

export function participantBelongsToCompetition(p, competitionId, competitions = []) {
  if (!competitionId) return false;
  const cid = String(competitionId);
  if (getParticipantCompetitionIds(p).includes(cid)) return true;

  const selected = competitions.find((c) => String(c._id) === cid);
  if (!selected?.name) return false;
  const raw = Array.isArray(p.competition_id) ? p.competition_id : [p.competition_id];
  return raw.some((c) => c?.name && c.name === selected.name);
}

const ParticipantList = ({ lockedGroupName } = {}) => {
  const [participants, setParticipants] = useState([]);
  const [personRoles, setPersonRoles] = useState([]);
  const [organisations, setOrganisations] = useState({});
  const [competitions, setCompetitions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [invitationTypes, setInvitationTypes] = useState([]);
  const [emsRoles, setEmsRoles] = useState([]);
  const [accreditationCategories, setAccreditationCategories] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedInvitationType, setSelectedInvitationType] = useState("");
  const [selectedEmsRole, setSelectedEmsRole] = useState("");
  const [selectedAccreditationCategory, setSelectedAccreditationCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedRegistrationStatus, setSelectedRegistrationStatus] = useState("");
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState("");
  const [isOpen, setIsOpen] = useState(null);
  const [autoLoginTarget, setAutoLoginTarget] = useState(null);
  const [autoLoginError, setAutoLoginError] = useState("");
  const [normalizedParticipants, setNormalizedParticipants] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [expandedParticipantId, setExpandedParticipantId] = useState(null);

  // 🔥 Sorting State — defaults to Registration Date, most recent first, so the
  // list has a deliberate order instead of relying on incidental DB order.
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });
  const deleteConfirm = useDeleteConfirm();

  const navigate = useNavigate();

  // Sorting function
  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const metaA = a.person_data ? safeParsePersonData(a.person_data) : {};
      const metaB = b.person_data ? safeParsePersonData(b.person_data) : {};

      const valA =
        sortConfig.key === "lastname" ? (metaA.familyLastName || a.person_id?.lname || "")
        : sortConfig.key === "firstname" ? (metaA.givenFirstName || a.person_id?.name || "")
        : sortConfig.key === "organisation" ? (getOrganisationName(a.person_id?._id) || "")
        : sortConfig.key === "group" ? (Array.isArray(a.group_id) ? a.group_id.map(g => g.group_name).join(", ") : a.group_id?.group_name || "")
        : sortConfig.key === "role" ? (a.role_id?.role_name || "")
        : sortConfig.key === "invitationType" ? (a.invitation_type_id?.name || "")
        : sortConfig.key === "registration" ? a.registration_status
        : sortConfig.key === "approval" ? a.approval_status
        : sortConfig.key === "date" ? a.createdAt
        : "";

      const valB =
        sortConfig.key === "lastname" ? (metaB.familyLastName || b.person_id?.lname || "")
        : sortConfig.key === "firstname" ? (metaB.givenFirstName || b.person_id?.name || "")
        : sortConfig.key === "organisation" ? (getOrganisationName(b.person_id?._id) || "")
        : sortConfig.key === "group" ? (Array.isArray(b.group_id) ? b.group_id.map(g => g.group_name).join(", ") : b.group_id?.group_name || "")
        : sortConfig.key === "role" ? (b.role_id?.role_name || "")
        : sortConfig.key === "invitationType" ? (b.invitation_type_id?.name || "")
        : sortConfig.key === "registration" ? b.registration_status
        : sortConfig.key === "approval" ? b.approval_status
        : sortConfig.key === "date" ? b.createdAt
        : "";

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === "asc"
            ? "desc"
            : "asc"
          : "asc",
    }));
  };

  const safeLower = (val) =>
  (val || "").toString().toLowerCase();

  const safeParsePersonData = (rawData) => {
    if (!rawData) return {};

    let processed = rawData;

    while (typeof processed === "string") {
      try {
        processed = JSON.parse(processed);
        if (processed && typeof processed === "object" && processed["0"] === "{") {
          processed = Object.values(processed).join("");
        }
      } catch {
        return {};
      }
    }

    return typeof processed === "object" && processed !== null && !Array.isArray(processed)
      ? processed
      : {};
  };

  const requestAutoLogin = (personId, displayName) => {
    setIsOpen(null);
    if (!personId) { alert("No person linked to this participant"); return; }

    const adminToken = sessionStorage.getItem("token");
    if (!adminToken) { alert("Not authenticated as admin"); return; }

    setAutoLoginTarget({ personId, name: displayName?.trim() || "this participant" });
    setAutoLoginError("");
  };

  const confirmAutoLogin = async () => {
    if (!autoLoginTarget) return;
    try {
      const res = await API.post("/auth/auto-login", { personId: autoLoginTarget.personId });
      const { code } = res.data;
      const url = `/user-auto-login?code=${encodeURIComponent(code)}`;
      window.open(url, "_blank");
      setAutoLoginTarget(null);
    } catch (error) {
      console.error("Auto login failed:", error.response?.data || error.message);
      setAutoLoginError(error?.response?.data?.msg || "Auto-login failed");
    }
  };


  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const fetchParticipants = async (
    competitionId = "",
    groupId = "",
    deleted = false,
    { showLoading = false } = {}
  ) => {
    try {
      if (showLoading) setListLoading(true);
      const params = {
        is_deleted: deleted ? true : false,
        limit: 500,
      };

      if (competitionId) {
        params.competitionId = competitionId;
        params.includeBulk = 'false';
      }
      const lockedEndpoint = lockedGroupName ? LOCKED_GROUP_ENDPOINTS[lockedGroupName] : null;
      if (!lockedEndpoint && groupId) params.groupId = groupId;

      const [participantsRes, orgRes, personRolesRes] = await Promise.all([
        API.get(lockedEndpoint || '/ems/participants', { params }),
        API.get('/organisations'),
        API.get('/personRoles'),
      ]);

      const data = Array.isArray(participantsRes.data)
        ? participantsRes.data
        : participantsRes.data?.data ||
          participantsRes.data?.participants ||
          [];

      const orgList = Array.isArray(orgRes.data) ? orgRes.data : [];
      const orgMap = orgList.reduce((map, org) => {
        map[org._id] = org.name;
        return map;
      }, {});

      setOrganisations(orgMap);
      setPersonRoles(Array.isArray(personRolesRes.data) ? personRolesRes.data : []);

      const rawList = Array.isArray(data) ? data : [];
      // Main Participants page (no lockedGroupName) excludes Athletes/Caddies —
      // they have their own dedicated sidebar pages (IGFEMS-473).
      const visibleList = lockedGroupName
        ? rawList
        : rawList.filter((p) => getOnsiteType(p) === null);
      setParticipants(visibleList);
    } catch (err) {
      console.error("Failed to fetch participants", err);
      setParticipants([]);
    } finally {
      setListLoading(false);
    }
  };


  const fetchCompetitions = async () => {
    try {
      const res = await API.get('/ems/competitions');
      const data = res.data?.data || res.data?.competitions || res.data;
      const list = Array.isArray(data) ? data : [];
      setCompetitions(list);
      return list;
    } catch (err) {
      console.error("Failed to fetch competitions", err);
      return [];
    }
  };

  const fetchGroups = async (competitionId = "") => {
    try {
      const params = competitionId ? { competitionId } : {};
      const res = await API.get('/ems/groups', { params });
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.data || res.data?.groups || [];
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch groups", err);
    }
  };

  const fetchInvitationTypes = async (competitionId = "") => {
    try {
      const params = competitionId ? { competitionId } : {};
      const res = await API.get('/ems/invitation-types', { params });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setInvitationTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch invitation types", err);
    }
  };

  const fetchEmsRoles = async () => {
    try {
      const res = await API.get('/ems/roles');
      setEmsRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch EMS roles", err);
    }
  };

  const fetchAccreditationCategories = async () => {
    try {
      const res = await API.get('/ems/accreditation-categories');
      setAccreditationCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch accreditation categories", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date
      .toLocaleDateString('en-GB', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      })
      .replace(',', '');
  };
  // Map of person_id -> comma-separated organisations
  const orgLookup = React.useMemo(() => {
    const map = {};

    const byPerson = {};
    personRoles.forEach((r) => {
      const pid = String(r.person_id?._id ?? r.person_id ?? "");
      if (!pid) return;
      if (!byPerson[pid]) byPerson[pid] = [];
      byPerson[pid].push(r);
    });

    Object.entries(byPerson).forEach(([pid, relations]) => {
      const names = dedupeOrgNamesForPerson(relations, organisations);
      map[pid] = names.join(', ').toLowerCase();
    });

    return map;
  }, [personRoles, organisations]);


  const resetClientFilters = () => {
    setSelectedRegistrationStatus("");
    setSelectedApprovalStatus("");
    setSelectedInvitationType("");
    setSelectedEmsRole("");
    setSelectedAccreditationCategory("");
    setSearchTerm("");
  };

  useEffect(() => {
    let mounted = true;
    fetchEmsRoles();
    fetchAccreditationCategories();
    fetchCompetitions().then((list) => {
      if (!mounted) return;
      const activeComp = Array.isArray(list)
        ? list.find((c) => c.status === true || c.status === "true" || c.active === true)
        : null;
      const activeId = activeComp?._id ? String(activeComp._id) : "";
      setCompetitions(list);
      setSelectedCompetition(activeId);
      if (activeId) {
        fetchGroups(activeId);
        fetchInvitationTypes(activeId);
        fetchParticipants(activeId, "", false, { showLoading: true });
      } else {
        setListLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
  if (!participants.length) {
    setNormalizedParticipants([]);
    return;
  }

  const normalized = participants.map((p) => {
    const meta = p.person_data ? safeParsePersonData(p.person_data) : {};

    const firstName =
      meta.givenFirstName || p.person_id?.name || "";

    const lastName =
      meta.familyLastName || p.person_id?.lname || "";

    const email =
      p.person_id?.email || meta.emailAddress || "";

    return {
      ...p,
      _searchIndex: {
        firstName: firstName.toLowerCase(),
        lastName: lastName.toLowerCase(),
        fullName: `${firstName} ${lastName}`.toLowerCase(),
        email: email.toLowerCase(),
        personId: String(p.person_id?._id ?? p.person_id ?? ""),
      }
    };
  });

  setNormalizedParticipants(normalized);
}, [participants]);


  const getOrganisationName = (personId) => {
    if (!personId || !personRoles.length) return '-';
    const relations = personRoles.filter(
      (role) => String(role.person_id?._id ?? role.person_id) === String(personId)
    );
    return pickMainOrgName(relations, organisations) || '-';
  };

  const filteredParticipants = React.useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return normalizedParticipants.filter((p) => {
      if (selectedCompetition && !participantBelongsToCompetition(p, selectedCompetition, competitions)) {
        return false;
      }
      if (selectedRegistrationStatus && p.registration_status !== selectedRegistrationStatus) return false;
      if (selectedApprovalStatus && p.approval_status !== selectedApprovalStatus) return false;
      if (selectedInvitationType) {
        const pInvId = p.invitation_type_id?._id || p.invitation_type_id;
        if (String(pInvId || "") !== String(selectedInvitationType)) return false;
      }
      if (selectedEmsRole) {
        const pRoleId = p.role_id?._id || p.role_id;
        if (String(pRoleId || "") !== String(selectedEmsRole)) return false;
      }
      if (selectedAccreditationCategory) {
        const pAccId = p.accreditation_category_id?._id || p.accreditation_category_id;
        if (String(pAccId || "") !== String(selectedAccreditationCategory)) return false;
      }

      if (!search) return true;
      const orgName = orgLookup[p._searchIndex.personId] || "";
      return (
        p._searchIndex.firstName.includes(search) ||
        p._searchIndex.lastName.includes(search) ||
        p._searchIndex.fullName.includes(search) ||
        p._searchIndex.email.includes(search) ||
        orgName.includes(search)
      );
    });
  }, [searchTerm, normalizedParticipants, orgLookup, selectedCompetition, competitions, selectedRegistrationStatus, selectedApprovalStatus, selectedInvitationType, selectedEmsRole, selectedAccreditationCategory]);



  const sortedParticipants = sortData(filteredParticipants);

  const handleEdit = (participant) => {
    navigate(`/ems/participants/edit-participant/${participant._id}`);
  };

  const participantDeleteLabel = (p) => {
    const meta = p.person_data ? safeParsePersonData(p.person_data) : {};
    const first = meta.givenFirstName || p.person_id?.name || "";
    const last = meta.familyLastName || p.person_id?.lname || "";
    return [first, last].filter(Boolean).join(" ") || "this participant";
  };

  const handleDelete = (p) => {
    deleteConfirm.ask({ id: p._id, name: participantDeleteLabel(p) });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.pending?.id;
    if (!id) return;
    deleteConfirm.setBusy(true);
    try {
      await API.delete(`/ems/participants/${id}`);
      clearCaddieOptionsCache();
      deleteConfirm.clear();
      fetchParticipants(selectedCompetition, selectedGroup, false);
    } catch {
      deleteConfirm.setError("Delete failed");
      deleteConfirm.setBusy(false);
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm("Restore this participant?")) return;
    try {
      await API.put(`/ems/participants/${id}/restore`);
      clearCaddieOptionsCache();
      fetchParticipants(selectedCompetition, selectedGroup, false);
      setShowDeleted(false);
    } catch {
      alert("Restore failed");
    }
  };

  const handlecompetitionChange = (e) => {
    const competitionId = e.target.value;
    setSelectedCompetition(competitionId);
    setSelectedGroup("");
    resetClientFilters();
    setCurrentPage(1);
    if (!competitionId) {
      setParticipants([]);
      setGroups([]);
      setInvitationTypes([]);
      return;
    }
    fetchGroups(competitionId);
    fetchInvitationTypes(competitionId);
    fetchParticipants(competitionId, "", showDeleted, { showLoading: true });
  };

  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    setSelectedGroup(groupId);
    setCurrentPage(1);
    fetchParticipants(selectedCompetition, groupId, showDeleted);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDeletedFilterChange = (e) => {
    const deleted = e.target.value === "deleted";
    setShowDeleted(deleted);
    setCurrentPage(1);
    fetchParticipants(selectedCompetition, selectedGroup, deleted);
  };

  const handleRegistrationStatusChange = (e) => {
    setSelectedRegistrationStatus(e.target.value);
    setCurrentPage(1);
  };

  // IGFEMS-478: unused while Approval Status filter is hidden — see docs/DISABLED_FEATURES.md
  // const handleApprovalStatusChange = (e) => {
  //   setSelectedApprovalStatus(e.target.value);
  //   setCurrentPage(1);
  // };

  const handleInvitationTypeChange = (e) => {
    setSelectedInvitationType(e.target.value);
    setCurrentPage(1);
  };

  const handleEmsRoleChange = (e) => {
    setSelectedEmsRole(e.target.value);
    setCurrentPage(1);
  };

  const handleAccreditationChange = (e) => {
    setSelectedAccreditationCategory(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const per = itemsPerPage >= 999999 ? filteredParticipants.length || 1 : itemsPerPage;
    const totalPages = Math.max(Math.ceil(filteredParticipants.length / per), 1);
    setCurrentPage((p) => (p > totalPages ? 1 : p));
  }, [filteredParticipants.length, itemsPerPage]);

  // When changing items per page, reset to page 1
  const handleItemsPerPageChange = (e) => {
    const val = e.target.value;
    setItemsPerPage(val === "all" ? 999999 : parseInt(val, 10));
    setCurrentPage(1);
  };



  const effectivePerPage = itemsPerPage >= sortedParticipants.length
    ? Math.max(sortedParticipants.length, 1)
    : itemsPerPage;
  const totalPages = Math.max(
    Math.ceil(sortedParticipants.length / effectivePerPage),
    1
  );
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentParticipants = sortedParticipants.slice(
    indexOfFirst,
    indexOfLast
  );
  const startCount = indexOfFirst + 1;
  const pageNumbers = Array.from(
    { length: totalPages },
    (_, i) => i + 1
  );

  const DropdownItem = ({ icon, onClick, label }) => (
    <li
      title={label}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
        padding: "5px 8px"
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ width: "16px" }} />
      <span className="text-sm" style={{ fontSize: "12px" }}>
        {label}
      </span>
    </li>
  );

  const getBadgeClass = (status) => {
    switch (status) {
      case "Added":
        return "badge bg-secondary";
      case "Pending":
        return "badge bg-warning text-white";
      case "Registered":
      case "Approved":
        return "badge bg-success";
      case "Rejected":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  if (listLoading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div>
      {/* TOP CONTROLS — align with gold sidebar panel (under logo) */}
      <div className="pl-single-row pl-single-row--toolbar mb-2">

        <div className="pl-single-item">
          <input
            type="text"
            className="form-control form-control-sm search-input"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ border: "1px solid #d1cfcf", width: "100%" }}
          />
        </div>

        <div className="pl-single-item">
          <select className="form-select form-select-sm" value={selectedCompetition} onChange={handlecompetitionChange}>
            <option value="">Competition</option>
            {competitions.map((ev) => <option key={ev._id} value={String(ev._id)}>{ev.name}</option>)}
          </select>
        </div>

        {!lockedGroupName && (
          <div className="pl-single-item">
            <select className="form-select form-select-sm" value={selectedGroup} onChange={handleGroupChange}>
              <option value="">Groups</option>
              {groups
                .filter((g) => !/^(athlete|caddie)$/i.test(g.group_name?.trim() || ''))
                .map((g) => <option key={g._id} value={String(g._id)}>{g.group_name}</option>)}
            </select>
          </div>
        )}

        {/* IGFEMS-638: Athletes/Caddies pages hide Roles and Invitation Types filters */}
        {!lockedGroupName && (
          <>
            <div className="pl-single-item">
              <select className="form-select form-select-sm" value={selectedEmsRole} onChange={handleEmsRoleChange}>
                <option value="">Roles</option>
                {emsRoles.map((r) => <option key={r._id} value={String(r._id)}>{r.role_name}</option>)}
              </select>
            </div>

            <div className="pl-single-item">
              <select className="form-select form-select-sm" value={selectedInvitationType} onChange={handleInvitationTypeChange}>
                <option value="">Invitation Types</option>
                {invitationTypes.map((t) => <option key={t._id} value={String(t._id)}>{t.name}</option>)}
              </select>
            </div>
          </>
        )}

        {/* IGFEMS-638: Athletes and Caddies don't have accreditations */}
        {!lockedGroupName && (
          <div className="pl-single-item">
            <select className="form-select form-select-sm" value={selectedAccreditationCategory} onChange={handleAccreditationChange}>
              <option value="">Accreditation</option>
              {accreditationCategories.map((c) => <option key={c._id} value={String(c._id)}>{formatAccreditationCategoryLabel(c)}</option>)}
            </select>
          </div>
        )}

        <div className="pl-single-item">
          <select className="form-select form-select-sm" value={selectedRegistrationStatus} onChange={handleRegistrationStatusChange}>
            <option value="">Registration Status</option>
            <option value="Added">Added</option>
            <option value="Pending">Pending</option>
            <option value="Registered">Registered</option>
          </select>
        </div>

        {/* IGFEMS-478: Approval Status filter hidden — not yet functional, see docs/DISABLED_FEATURES.md
        <div className="pl-single-item">
          <select className="form-select form-select-sm" value={selectedApprovalStatus} onChange={handleApprovalStatusChange}>
            <option value="">Approval Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
        */}

        <div className="pl-single-item">
          <select className="form-select form-select-sm" value={showDeleted ? "deleted" : "active"} onChange={handleDeletedFilterChange}>
            <option value="active">Active Participants</option>
            <option value="deleted">Deleted Participants</option>
          </select>
        </div>

        {searchTerm && !listLoading && filteredParticipants.length === 0 && (
          <button
            onClick={() => navigate("/ems/search", { state: { searchValue: searchTerm } })}
            className="pl-add-btn"
            title="Participant not found. Click to add new."
          >
            + Add
          </button>
        )}

      </div>

      {/* TABLE */}
        <div className={`participant-header${lockedGroupName ? " participant-grid--locked-group" : ""}`}>
                  <div
          className={`sort-header ${sortConfig.key === "lastname" ? "sorted" : ""}`}
          onClick={() => toggleSort("lastname")}
        >
          <span>Lastname</span>
          <FontAwesomeIcon icon={sortIcon("lastname")} className="sort-icon" />
        </div>


                  <div
          className={`sort-header ${sortConfig.key === "firstname" ? "sorted" : ""}`}
          onClick={() => toggleSort("firstname")}
        >
          <span>Firstname</span>
          <FontAwesomeIcon icon={sortIcon("firstname")} className="sort-icon" />
        </div>


          <div
            className={`sort-header ${sortConfig.key === "organisation" ? "sorted" : ""}`}
            onClick={() => toggleSort("organisation")}
          >
            <span>Organisation</span>
            <FontAwesomeIcon icon={sortIcon("organisation")} className="sort-icon" />
          </div>


          {/* IGFEMS-638: Athletes/Caddies pages hide Groups, Roles, Invitation Types and Accreditation columns */}
          {!lockedGroupName && (
            <>
          <div
          className={`sort-header ${sortConfig.key === "group" ? "sorted" : ""}`}
          onClick={() => toggleSort("group")}
        >
          <span>Groups</span>
          <FontAwesomeIcon icon={sortIcon("group")} className="sort-icon" />
        </div>


          <div
          className={`sort-header ${sortConfig.key === "role" ? "sorted" : ""}`}
          onClick={() => toggleSort("role")}
        >
          <span>Roles</span>
          <FontAwesomeIcon icon={sortIcon("role")} className="sort-icon" />
        </div>


          <div
          className={`sort-header ${sortConfig.key === "invitationType" ? "sorted" : ""}`}
          onClick={() => toggleSort("invitationType")}
        >
          <span>Invitation Types</span>
          <FontAwesomeIcon icon={sortIcon("invitationType")} className="sort-icon" />
        </div>
          <div>Accreditation</div>
            </>
          )}

          <div
            className={`sort-header ${sortConfig.key === "registration" ? "sorted" : ""}`}
            onClick={() => toggleSort("registration")}
          >
            <span>Registration Status</span>
            <FontAwesomeIcon icon={sortIcon("registration")} className="sort-icon" />
          </div>


          {/* IGFEMS-478: Approval Status column header hidden — not yet functional, see docs/DISABLED_FEATURES.md
          <div
          className={`sort-header ${sortConfig.key === "approval" ? "sorted" : ""}`}
          onClick={() => toggleSort("approval")}
        >
          <span>Approval Status</span>
          <FontAwesomeIcon icon={sortIcon("approval")} className="sort-icon" />
        </div>
          */}


          <div
          className={`sort-header ${sortConfig.key === "date" ? "sorted" : ""}`}
          onClick={() => toggleSort("date")}
        >
          <span>Registration Date</span>
          <FontAwesomeIcon icon={sortIcon("date")} className="sort-icon" />
        </div>


          <div>Actions</div>
        </div>

        {listLoading ? (
          <GoldLoader label="Loading..." variant="section" />
        ) : !selectedCompetition ? (
          <p className="text-muted p-4 text-center">Select a competition to view participants.</p>
        ) : sortedParticipants.length === 0 ? (
          <p className="text-muted p-2">No participants available</p>
        ) : (
          currentParticipants.map((p) => {
            const meta = p.person_data ? safeParsePersonData(p.person_data) : {};
            const participantGroups = p.group_id
              ? Array.isArray(p.group_id)
                ? p.group_id.map((g) => g.group_name).join(", ")
                : p.group_id.group_name
              : "-";
            const hasGuests = Number(p.guest_count) > 0;

            return (
              <React.Fragment key={p._id}>
              <div
                className={`participant-row${lockedGroupName ? " participant-grid--locked-group" : ""}`}
                onClick={() => navigate(`/ems/participants/edit-participant/${p._id}?view=true`)}
                style={{ cursor: "pointer" }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
                >
                  {hasGuests ? (
                    <FontAwesomeIcon
                      icon={expandedParticipantId === p._id ? faChevronDown : faChevronRight}
                      style={{ fontSize: "12px", color: "#666" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedParticipantId(expandedParticipantId === p._id ? null : p._id);
                      }}
                      title={expandedParticipantId === p._id ? "Hide guests" : "Show guests"}
                    />
                  ) : (
                    <span style={{ width: "12px" }} />
                  )}
                  <span>
                    {meta.familyLastName || p.person_id?.lname || "-"}
                  </span>
                </div>

                <div>
                  {meta.givenFirstName || p.person_id?.name || "-"}
                </div>

                <div>{getOrganisationName(p.person_id?._id)}</div>
                {!lockedGroupName && (
                  <>
                    <div>{participantGroups}</div>
                    <div>{p.role_id?.role_name || "-"}</div>
                    <div>{p.invitation_type_id?.name || "-"}</div>
                    <div>{formatAccreditationCategoryLabel(p.accreditation_category_id) !== "Unnamed" ? formatAccreditationCategoryLabel(p.accreditation_category_id) : "-"}</div>
                  </>
                )}

                <div>
                  <span className={`participant-badge-registration ${getBadgeClass(p.registration_status)}`}>
                    {p.registration_status}
                  </span>
                </div>

                {/* IGFEMS-478: Approval Status badge hidden — not yet functional, see docs/DISABLED_FEATURES.md
                <div>
                  <span className={`participant-badge-approval ${getBadgeClass(p.approval_status)}`}>
                    {p.approval_status}
                  </span>
                </div>
                */}

                <div>{formatDate(p.createdAt)}</div>

                {/* ACTIONS */}
                <div style={{ display: "flex", gap: "5px" }}>
                  {!p.is_deleted && (
                    <>
                      <button
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(p);
                        }}
                        style={{ background: "transparent", border: "none" }}
                        title="Edit participant"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(p);
                        }}
                        style={{ background: "transparent", border: "none" }}
                        title="Delete participant"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </>
                  )}

                  {/* RESTORE WHEN DELETED */}
                  {p.is_deleted && (
                    <button
                      className="btn btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(p._id);
                      }}
                      style={{ background: "transparent", border: "none" }}
                      title="Restore participant"
                    >
                      <FontAwesomeIcon icon={faUndo} />
                    </button>
                  )}

                  {!p.is_deleted && (
                    <button
                      className="btn btn-sm"
                      title={
                        p.documentStatus === "approved"
                          ? "Document Verified"
                          : "Verification Pending"
                      }
                      style={{ background: "transparent", border: "none" }}
                    >
                      <FontAwesomeIcon
                        icon={
                          p.documentStatus === "approved"
                            ? faFileCircleCheck
                            : faFileCircleQuestion
                        }
                        style={{
                          color:
                            p.documentStatus === "approved"
                              ? "#198754" /* success green */
                              : "#D9A128", /* brand amber — pending */
                        }}
                      />
                    </button>
                  )}

                  {!p.is_deleted && (
                    <div className="dropdown">
                      <button
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpen(isOpen === p._id ? null : p._id);
                        }}
                        style={{ background: "transparent", border: "none" }}
                        title="More actions"
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </button>

                      {isOpen === p._id && (
                        <ul
                          className="dropdown-menu show dropdown-menu-end"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "100%",
                            marginTop: "0.3rem",
                            fontSize: "10px"
                          }}
                        >
                          {(() => {
                            const onsiteType = getOnsiteType(p);
                            return onsiteType ? (
                              <DropdownItem
                                icon={faClipboardCheck}
                                label="On-site registration"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/onsite-registration-form?id=${p._id}&type=${onsiteType}`, '_blank');
                                }}
                              />
                            ) : null;
                          })()}
                          <DropdownItem
                            icon={faUserShield}
                            label="Permissions"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/ems/participants/edit-participant/${p._id}?tab=personal-settings`
                              );
                            }}
                          />
                          <DropdownItem
                            icon={faSignOutAlt}
                            label="Login"
                            onClick={(e) => {
                              e.stopPropagation();
                              const personId = p.person_id?._id || p.person_id;
                              const displayName = [p.person_id?.name, p.person_id?.lname]
                                .filter(Boolean)
                                .join(" ");
                              requestAutoLogin(personId, displayName);
                            }}
                          />
                          <DropdownItem
                            icon={faFileArchive}
                            label="Documents"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsOpen(null);
                              navigate('/ems/documents-downloads');
                            }}
                          />
                          <DropdownItem
                            icon={faBuilding}
                            label="Accommodation"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/ems/participants/edit-participant/${p._id}?tab=accommodation`
                              );
                            }}
                          />
                          {/* DISABLED 2026-07-31 — Manage Hotel (no implementation) */}
                          {/* <DropdownItem
                            icon={faHotel}
                            label="Manage Hotel"
                            onClick={() => console.log("Manage Hotel", p._id)}
                          /> */}
                          {/* DISABLED 2026-08-10 — Email To (⋯ menu). Was: navigate edit-participant?tab=email */}
                          {/* DISABLED 2026-07-31 — Duplicate (no implementation) */}
                          {/* <DropdownItem
                            icon={faClone}
                            label="Duplicate"
                            onClick={() => console.log("Duplicate", p._id)}
                          /> */}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {hasGuests && expandedParticipantId === p._id && (
                <ParticipantGuestRow
                  participantId={p._id}
                  onOpenGuest={(guestId) =>
                    navigate(`/ems/participants/${p._id}/guests/${guestId}/edit`)
                  }
                />
              )}
              </React.Fragment>
            );
          })
        )}

        {/* PAGINATION inside table */}
        {!listLoading && sortedParticipants.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: '13px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {pageNumbers.slice(0, 5).map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? "active" : ""}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      {autoLoginTarget && (
        <AutoLoginConfirmModal
          name={autoLoginTarget.name}
          error={autoLoginError}
          onConfirm={confirmAutoLogin}
          onCancel={() => setAutoLoginTarget(null)}
        />
      )}
      {deleteConfirm.pending && (
        <DeleteConfirmModal
          name={deleteConfirm.pending.name}
          error={deleteConfirm.error}
          busy={deleteConfirm.busy}
          onConfirm={confirmDelete}
          onCancel={deleteConfirm.clear}
        />
      )}
    </div>
  );
};

export default ParticipantList;
